import Guid from "../helpers/guid";
import {Month} from "../enums/month";
import {DurationUnit} from "../enums/durationUnit";
import IRepo from "../interfaces/repo";
import Repo from "../state/modules/repo";
import Vue from "vue";
import BuilderModel from "./tracked/builderModel";
import {WeekDay} from "~/enums/weekday";
import {AmPm} from "~/enums/AmPm";
import {IntervalEndType} from "~/enums/intervalEndType";
import {Hash} from "~/interfaces/hashInterfaces";
import ObjectModelConditionalRuleSet from "~/models/objectModels/objectModelConditionalRuleSet";
import ObjectModelConditionalFields, {ObjectModelTriggerConditionalField} from "~/models/objectModels/objectModelConditionalFields";
import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
import IConditionalRuleContainer from "~/models/conditionalRules/conditionalRuleContainer";
import {ObjectHelper} from "~/helpers/objectHelper";
import ObjectModelProperty from "~/models/objectModelProperties/objectModelProperty";
import {RelationType} from "~/models/relationType";
import RelativeTimePeriod from "~/models/relativeTimePeriod";

export default class Interval extends BuilderModel implements IConditionalRuleContainer{
  day: number;
  month: Month;
  amount: number;
  unit: number;
  time_of_day: number;
  time_minute: number;
  time_am_or_pm: AmPm;
  end_date: string;
  weekdays: WeekDay[] = [];
  except = ['fieldName'];
  end_type: IntervalEndType;
  end_condition_conditional_rule_sets: Hash<ObjectModelConditionalRuleSet>;
  number_of_occurrences: number;

  static repo: IRepo<Interval> = new Repo<Interval>('intervals', Interval);

  constructor(parent: BuilderModel, private fieldName: string, json: any = {}) {
    super();
    this.defineProperty('client_id', json['client_id'] || Guid.create());
    this.defineProperty('weekdays', []);
    this.defineProperty('amount', 1);
    this.defineProperty('day', 1);
    this.defineProperty('month', Month.january);
    this.defineProperty('unit', DurationUnit.day);
    this.defineProperty('time_minute', 0);
    this.defineProperty('time_of_day', 12);
    this.defineProperty('time_am_or_pm', AmPm.AM);
    this.defineProperty('end_type', IntervalEndType.Never);
    this.defineProperty('end_date', null);
    this.defineProperty('number_of_occurrences', 0);

    this.fieldName = fieldName;
    this.parent = parent;
    this.rootModel = parent.rootModel;

    this.loadFromJson(json, this.rootModel);

    Interval.repo.add(this);
    Vue.set(parent, fieldName, this);

    for (const definition of ObjectModelConditionalFields.INTERVAL_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (p, j) => {
        return ConditionalRuleSetFactory.createForContainer(this, definition, {}, true, false);
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] = definition.defaultConditionalValue;
      }
    }
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {};
    for (const definition of ObjectModelConditionalFields.INTERVAL_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {type: ObjectModelConditionalRuleSet, relationType: RelationType.dictionary};
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {type: Object, relationType: RelationType.singleObject};
      }
    }
    return models;
  }
  weekdayIsSelected(day: WeekDay){
    return this.weekdays.includes(day)
  }

  get rootName(): string {
    return this.fieldName;
  }

  get isCollectionRoot(): boolean {
    return false;
  }
  getConditionalRuleSets(fieldName: string): Hash<ObjectModelConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(fieldName: string): ObjectModelConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) => x.sort_order < y.sort_order ? -1 : 1);
  }

  createNewRuleSet(fieldName: string, definition: ObjectModelTriggerConditionalField): ObjectModelConditionalRuleSet {
    return new ObjectModelConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): ObjectModelProperty {
    return ObjectModelProperty.repo.find(id);
  }

  getReferencableControls(): ObjectModelProperty[] {
    return this.parent.rootModel.objectModelPropertiesArray;
  }
}